module Sm.Model.RealtimeQuery

open MobF

open Sm.Service.Repository

type Operation<'Q, 'R> =
    | Unknown
    | Pending of 'Q
    | Ready of 'Q * Result<'R, string>

//note: the state should really be the operation itself, but there is currently
//a bug in MobF whereby computation over union types is not reacting correctly
type State<'Q, 'R> = {
    Operation: Operation<'Q, 'R>
}

type Msg<'Q, 'R> =
    | Update of Operation<'Q, 'R>
    | Execute of 'Q
    | Merge of MergeEvent

type IComputed<'Q, 'R> =
    abstract Query: 'Q option
    abstract Ready: Result<bool, string>
    abstract Result: 'R

type Type<'Q, 'R> = Model<State<'Q, 'R>, Msg<'Q, 'R>, IComputed<'Q, 'R>>

type Setup<'Q, 'R> = {
    Empty: 'R
    Repository: Repository
    Execute: 'Q -> Fable.Core.JS.Promise<'R>
    Merge: 'Q -> MergeEvent -> 'R -> 'R
}

module Impl =
    //Why this strange convolution? Fable's limited support for generics would
    //require these functions to be inlined, which would duplicate all their
    //code at every callsite. Hence hoisting most of this out into a separate
    //module with a type-bound argument which resolves all the generics.

    type Env<'Q, 'R> = {
        Repository: Repository
        Buffer: ResizeArray<MergeEvent>
        Setup: Setup<'Q, 'R>
    }

    let init env post =
        env.Repository.Merged
        |> Event.add (Merge >> post)
        { Operation = Unknown }

    let update env (state, post) = function
        | Update Unknown ->
            { state with Operation = Unknown }

        | Update (Pending request as op) ->
            promise {
                let! result = env.Setup.Execute request
                let nextState = Ready (request, Ok result)
                post (Update nextState)
            }
            |> Promise.start

            { state with Operation = op }

        | Update (Ready (_, Ok _) as op) ->
            env.Buffer |> Seq.iter (Merge >> post)
            env.Buffer.Clear()

            { state with Operation = op }

        | Update (Ready (_, Error _) as op) ->
            env.Buffer.Clear()
            { state with Operation = op }

        | Execute request ->
            post (Update (Pending request))
            { state with Operation = Unknown }

        | Merge e ->
            match state.Operation with
            | Unknown | Pending _ ->
                env.Buffer.Add(e)
                state
            | Ready (_, Error _) -> 
                state
            | Ready (request, Ok value) ->
                let nextValue = env.Setup.Merge request e value
                { state with Operation = Ready (request, Ok nextValue) }

    let compute env state =
        { new IComputed<_, _> with
            member _.Query =
                match state.Operation with
                | Unknown -> None
                | Pending q -> Some q
                | Ready (q, _) -> Some q

            member _.Ready =
                match state.Operation with
                | Unknown | Pending _ -> Ok false
                | Ready (_, Ok _) -> Ok true
                | Ready (_, Error message) -> Error message

            member _.Result =
                match state.Operation with
                | Ready (_, Ok x) -> x
                | _ -> env.Setup.Empty
        }

let inline create setup =
    let env = {
        Impl.Repository = setup.Repository
        Impl.Buffer = ResizeArray<_>()
        Impl.Setup = setup
    }

    Model.useInitWithPost (Impl.init env)
    |> Model.andUpdateWithPost (Impl.update env)
    |> Model.createWithComputed (Impl.compute env)
